var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};

// src/lib/BlockUi.tsx
import { useCallback, useEffect, useRef, useState, isValidElement } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";
import { jsx, jsxs } from "react/jsx-runtime";
var Loader = () => {
  return /* @__PURE__ */ jsx(CircularProgress, { "aria-label": "Loading", variant: "indeterminate" });
};
var Block = styled("div")({
  position: "relative"
});
var BlockUiContainer = styled("div")({
  position: "absolute",
  zIndex: 1010,
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  height: "100%",
  minHeight: "4.5em",
  cursor: "wait",
  overflow: "hidden",
  ":focus": {
    outline: "none"
  }
});
var BlockUiOverlay = styled("div")({
  width: "100%",
  height: "100%",
  opacity: "0.88",
  filter: "alpha(opacity=50)",
  backgroundColor: "white"
});
var BlockUiMessageContainer = styled("div")(({ top = "50%" }) => ({
  top,
  position: "absolute",
  left: 0,
  right: 0,
  textAlign: "center",
  transform: "translateY(-50%)",
  zIndex: 10001
}));
var BlockUiMessage = styled("div")({
  color: "#333",
  background: "none",
  zIndex: "1011"
});
var ScreenReaderOnly = styled("div")({
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  borderWidth: 0
});
function BlockUi(_a) {
  var _b = _a, {
    backdropColor = "paper",
    blocking,
    children,
    keepInView,
    loader = /* @__PURE__ */ jsx(Loader, {}),
    message,
    renderChildren = true
  } = _b, rest = __objRest(_b, [
    "backdropColor",
    "blocking",
    "children",
    "keepInView",
    "loader",
    "message",
    "renderChildren"
  ]);
  const [top, setTop] = useState("50%");
  const blockerRef = useRef(null);
  const topFocusRef = useRef(null);
  const containerRef = useRef(null);
  const messageContainerRef = useRef(null);
  const handleKeepInView = useCallback(() => {
    if (blocking && keepInView && containerRef.current) {
      const containerBounds = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      if (containerBounds.top > windowHeight || containerBounds.bottom < 0) return;
      if (containerBounds.top >= 0 && containerBounds.bottom <= windowHeight) {
        if (top !== "50%") {
          setTop("50%");
        }
        return;
      }
      const messageBoundsHeight = messageContainerRef.current ? messageContainerRef.current.getBoundingClientRect().height : 0;
      let newTop = Math.max(
        Math.min(windowHeight, containerBounds.bottom) - Math.max(containerBounds.top, 0),
        messageBoundsHeight
      ) / 2;
      if (containerBounds.top < 0) {
        newTop = Math.min(newTop - containerBounds.top, containerBounds.height - messageBoundsHeight / 2);
      }
      if (top !== newTop) {
        setTop(newTop);
      }
    }
  }, [blocking, keepInView, top]);
  useEffect(() => {
    window.addEventListener("scroll", handleKeepInView);
    return () => {
      window.removeEventListener("scroll", handleKeepInView);
    };
  }, [handleKeepInView]);
  const blockingTab = (event, withShift = false) => {
    const { key, shiftKey } = event;
    return blocking && key === "Tab" && shiftKey === withShift;
  };
  const tabbedUpTop = (event) => {
    if (blockerRef.current && blockingTab(event)) {
      blockerRef.current.focus();
    }
  };
  const tabbedDownTop = (event) => {
    if (blockerRef.current && blockingTab(event)) {
      event.preventDefault();
      blockerRef.current.focus();
    }
  };
  const tabbedUpBottom = (event) => {
    if (topFocusRef.current && blockingTab(event, true)) {
      topFocusRef.current.focus();
    }
  };
  const tabbedDownBottom = (event) => {
    if (topFocusRef.current && blockingTab(event, true)) {
      event.preventDefault();
      topFocusRef.current.focus();
    }
  };
  const shouldRenderChildren = !blocking || renderChildren;
  return /* @__PURE__ */ jsxs(Block, __spreadProps(__spreadValues({ "aria-busy": blocking }, rest), { children: [
    blocking ? /* @__PURE__ */ jsx(ScreenReaderOnly, { tabIndex: 0, onKeyUp: tabbedUpTop, onKeyDown: tabbedDownTop, ref: topFocusRef, children: message || "loading" }) : null,
    shouldRenderChildren ? children : null,
    blocking ? /* @__PURE__ */ jsxs(BlockUiContainer, { tabIndex: 0, ref: blockerRef, onKeyUp: tabbedUpBottom, onKeyDown: tabbedDownBottom, children: [
      /* @__PURE__ */ jsx(BlockUiOverlay, { ref: containerRef, sx: { backgroundColor: `background.${backdropColor}` } }),
      /* @__PURE__ */ jsx(BlockUiMessageContainer, { ref: messageContainerRef, top: keepInView ? top : void 0, children: /* @__PURE__ */ jsxs(BlockUiMessage, { children: [
        isValidElement(loader) ? /* @__PURE__ */ jsx("div", { "aria-hidden": true, children: loader }) : null,
        message || /* @__PURE__ */ jsx(ScreenReaderOnly, { children: "loading" })
      ] }) })
    ] }) : null
  ] }));
}
export {
  BlockUi
};
